import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useNotifications } from "@/components/Layout";
import { Bell, Clock, Filter, MapPin, Network, StickyNote, AlertTriangle, Pencil, User as UserIcon } from "lucide-react";

const ACTION_LABEL = {
  "engagement.note_add": "Note Added",
  "engagement.issue_add": "Issue Raised",
  "engagement.cluster_set": "Cluster Changed",
  "user.create": "User Created",
  "user.update": "User Updated",
  "user.delete": "User Deleted",
  "user.bulk_create": "Bulk User Create",
  "user.bulk_update": "Bulk User Update",
};

const ACTION_ICON = {
  "engagement.note_add": StickyNote,
  "engagement.issue_add": AlertTriangle,
  "engagement.cluster_set": Network,
};

const ACTION_COLOR = {
  "engagement.note_add": "var(--on-track)",
  "engagement.issue_add": "var(--amber)",
  "engagement.cluster_set": "var(--accent)",
  "user.create": "var(--on-track)",
  "user.update": "var(--accent)",
  "user.delete": "var(--behind)",
};

function fmtVal(v) {
  if (v == null || v === "") return "(none)";
  if (Array.isArray(v)) return v.length ? v.join(", ") : "(none)";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  return String(v);
}

export default function Notifications() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actorFilter, setActorFilter] = useState("");
  const { markRead } = useNotifications();

  useEffect(() => {
    api.get("/notifications", { params: { limit: 300 } })
      .then((r) => {
        setLogs(r.data);
        // Mark as read so the sidebar badge clears
        markRead();
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => logs.filter((l) => {
    if (actorFilter && !(l.actor_email || "").toLowerCase().includes(actorFilter.toLowerCase())) return false;
    return true;
  }), [logs, actorFilter]);

  return (
    <div className="max-w-[1200px] mx-auto px-10 py-10 fade-in-up" data-testid="notifications-page">
      <div className="mb-8">
        <div className="mono text-[10px] uppercase tracking-widest text-[color:var(--ink-2)] mb-2">
          Activity In Your Assigned Scope
        </div>
        <h1 className="serif text-5xl tracking-tight leading-none">Notifications</h1>
        <p className="text-sm text-[color:var(--ink-2)] mt-3 max-w-2xl">
          Everything other users have changed inside the countries and clusters that have been assigned to you.
          Your own actions are hidden here.
        </p>
      </div>

      <div className="flex flex-wrap gap-3 mb-4 items-center">
        <div className="flex-1 min-w-64 relative">
          <UserIcon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--ink-3)]" />
          <input
            className="input-flat pl-9"
            placeholder="Filter by user email…"
            value={actorFilter}
            onChange={(e) => setActorFilter(e.target.value)}
            data-testid="notif-actor-filter"
          />
        </div>
        <button className="btn-ghost text-xs flex items-center gap-1" onClick={() => setActorFilter("")}>
          <Filter size={12} /> Clear
        </button>
      </div>

      <div className="mono text-[11px] text-[color:var(--ink-2)] mb-3 uppercase tracking-widest">
        {loading ? "Loading…" : `${filtered.length} entr${filtered.length === 1 ? "y" : "ies"}`}
      </div>

      <div className="space-y-2">
        {filtered.map((l) => {
          const Icon = ACTION_ICON[l.action] || Pencil;
          const color = ACTION_COLOR[l.action] || "var(--ink)";
          return (
            <div key={l.id} className="hairline bg-white p-4" data-testid={`notif-${l.id}`}>
              <div className="flex items-start gap-3 flex-wrap">
                <div
                  className="w-10 h-10 flex items-center justify-center border shrink-0"
                  style={{ borderColor: color, color }}
                >
                  <Icon size={18} strokeWidth={2} />
                </div>
                <div className="flex-1 min-w-64">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="text-sm font-medium">{l.actor_email}</span>
                    <span className="mono text-[10px] uppercase tracking-widest px-1.5 py-0.5 border" style={{ borderColor: color, color }}>
                      {ACTION_LABEL[l.action] || l.action}
                    </span>
                    <span className="text-sm text-[color:var(--ink-2)]">on</span>
                    <span className="text-sm font-medium">{l.target_label}</span>
                  </div>
                  <div className="mono text-[10px] text-[color:var(--ink-3)] flex flex-wrap gap-3 mt-1">
                    <span className="flex items-center gap-1"><Clock size={10} /> {new Date(l.timestamp).toLocaleString()}</span>
                    {l.country && <span className="flex items-center gap-1"><MapPin size={10} /> {l.country}</span>}
                    {l.cluster && <span className="flex items-center gap-1"><Network size={10} /> {l.cluster}</span>}
                  </div>
                  {l.changes && Object.keys(l.changes).length > 0 && (
                    <div className="mt-2 grid gap-1.5">
                      {Object.entries(l.changes).map(([field, diff]) => (
                        <div key={field} className="text-xs flex gap-2 items-baseline border-l-2 pl-3 py-1" style={{ borderColor: color }}>
                          <span className="mono text-[10px] uppercase tracking-widest text-[color:var(--ink-2)] shrink-0">{field}</span>
                          {"old" in (diff || {}) && (
                            <>
                              <span className="text-[color:var(--behind)] line-through break-all">{fmtVal(diff.old)}</span>
                              <span className="text-[color:var(--ink-3)]">→</span>
                            </>
                          )}
                          <span className="text-[color:var(--on-track)] break-all">{fmtVal(diff.new)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
        {!loading && filtered.length === 0 && (
          <div className="hairline bg-white p-10 text-center">
            <Bell size={22} className="mx-auto mb-3 text-[color:var(--ink-3)]" />
            <div className="serif text-xl">You're all caught up</div>
            <div className="text-sm text-[color:var(--ink-2)] mt-1">
              Changes made by others inside your assigned countries and clusters will appear here.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
